import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, User, Calendar as CalIcon, DollarSign, Filter, Search, AlertCircle, RefreshCw, Eye, X, Minus } from 'lucide-react';
import { useCart } from '../components/CartContext';

// Estados de pedido con colores
const estados = [
  { value: 'solicitado', label: 'Solicitado', color: 'bg-gray-100 text-gray-800' },
  { value: 'confirmado', label: 'Confirmado', color: 'bg-blue-100 text-blue-800' },
  { value: 'facturado', label: 'Facturado', color: 'bg-green-100 text-green-800' },
  { value: 'despachado', label: 'Despachado', color: 'bg-orange-100 text-orange-800' },
  { value: 'entregado', label: 'Entregado', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'anulado', label: 'Anulado', color: 'bg-red-100 text-red-800' },
];

// Datos simulados como fallback (si la API falla) - con artículos de ejemplo
const mockPedidos = [
  {
    idPedido: 'PED001',
    fechaPedido: '2024-01-15',
    cliente: 'Juan Pérez',
    total: 250.00,
    estado: 'solicitado',
    articulos: [
      { codArticulo: 'ART001', desArticulo: 'Zapatillas Nike Air Max', cantidad: 1, precioUnitario: 250.00, subtotal: 250.00 },
    ],
  },
  {
    idPedido: 'PED002',
    fechaPedido: '2024-01-14',
    cliente: 'María García',
    total: 370.50,
    estado: 'confirmado',
    articulos: [
      { codArticulo: 'ART002', desArticulo: 'Camiseta Adidas Stan Smith', cantidad: 2, precioUnitario: 60.00, subtotal: 120.00 },
      { codArticulo: 'ART003', desArticulo: 'Pantalones Puma Fitness', cantidad: 1, precioUnitario: 250.50, subtotal: 250.50 },
    ],
  },
  {
    idPedido: 'PED003',
    fechaPedido: '2024-01-12',
    cliente: 'Carlos López',
    total: 320.75,
    estado: 'facturado',
    articulos: [
      { codArticulo: 'ART004', desArticulo: "Chaqueta Levi's Jeans", cantidad: 1, precioUnitario: 320.75, subtotal: 320.75 },
    ],
  },
  {
    idPedido: 'PED004',
    fechaPedido: '2024-01-10',
    cliente: 'Ana Torres',
    total: 185.00,
    estado: 'despachado',
    articulos: [
      { codArticulo: 'ART005', desArticulo: 'Reloj Casio Digital', cantidad: 1, precioUnitario: 95.00, subtotal: 95.00 },
      { codArticulo: 'ART006', desArticulo: 'Bolso Gucci', cantidad: 1, precioUnitario: 90.00, subtotal: 90.00 },
    ],
  },
  {
    idPedido: 'PED005',
    fechaPedido: '2024-01-08',
    cliente: 'Luis Ramírez',
    total: 450.25,
    estado: 'entregado',
    articulos: [
      { codArticulo: 'ART007', desArticulo: 'Laptop MacBook Pro', cantidad: 1, precioUnitario: 450.25, subtotal: 450.25 },
    ],
  },
  {
    idPedido: 'PED006',
    fechaPedido: '2024-01-05',
    cliente: 'Sofía Mendoza',
    total: 120.00,
    estado: 'anulado',
    articulos: [
      { codArticulo: 'ART008', desArticulo: 'Perfume Chanel', cantidad: 1, precioUnitario: 120.00, subtotal: 120.00 },
    ],
  },
];

const getEstadoBadge = (estado) => {
  const estadoObj = estados.find(e => e.value === estado);
  return estadoObj ? estadoObj.color : 'bg-gray-100 text-gray-800';
};

const Orders = ({ currentUser, token }) => {
  const [pedidos, setPedidos] = useState([]); // Lista de pedidos de la API o mock
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('todos'); // Filtro por estado
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usingFallback, setUsingFallback] = useState(false);
  const [selectedPedido, setSelectedPedido] = useState(null); // Para modal de detalles
  const { addToCart } = useCart(); // En caso de necesitar carrito

  // URL de tu API para obtener pedidos (¡CÁMBIALA por la tuya!)
  const API_ORDERS_URL = 'https://api.massi-store.com/orders'; // Endpoint que devuelve lista de pedidos

  const fetchPedidos = async () => {
    if (!currentUser || !currentUser.idCliente || !token) {
      setError('No se pudo cargar los pedidos. Verifica tu sesión.');
      setLoading(false);
      loadFallbackData();
      return;
    }

    setLoading(true);
    setError('');
    setUsingFallback(false);

    try {
      const response = await fetch(`${API_ORDERS_URL}?idCliente=${currentUser.idCliente}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`, // Token en header para autenticación
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.response && Array.isArray(data.response)) {
          // Mapea los campos de la API al formato del componente (incluyendo articulos)
          const mappedPedidos = data.response.map(pedido => ({
            idPedido: pedido.idPedido,
            fechaPedido: pedido.fechaPedido || new Date().toISOString().split('T')[0],
            cliente: pedido.cliente || 'Cliente Anónimo',
            total: parseFloat(pedido.total) || 0,
            estado: pedido.estado || 'solicitado',
            articulos: pedido.articulos || [], // Array de artículos del pedido
          }));
          setPedidos(mappedPedidos);
        } else {
          throw new Error('Respuesta inválida de la API (response no es array)');
        }
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error al cargar pedidos:', err);
      setError(`No se pudieron cargar los pedidos: ${err.message}. Usando datos de ejemplo.`);
      setUsingFallback(true);
      loadFallbackData();
    } finally {
      setLoading(false);
    }
  };

  const loadFallbackData = () => {
    // Mapea mock data al formato del componente (con articulos)
    const mappedMock = mockPedidos.map(pedido => ({
      idPedido: pedido.idPedido,
      fechaPedido: pedido.fechaPedido,
      cliente: pedido.cliente,
      total: pedido.total,
      estado: pedido.estado,
      articulos: pedido.articulos,
    }));
    setPedidos(mappedMock);
  };

  useEffect(() => {
    fetchPedidos();
  }, [currentUser, token]); // Recarga si cambian user o token

  // Filtra pedidos localmente por búsqueda y estado
  const filteredPedidos = pedidos.filter(pedido => {
    const matchesSearch = !searchTerm || 
      pedido.idPedido.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pedido.cliente.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEstado = filterEstado === 'todos' || pedido.estado === filterEstado;
    return matchesSearch && matchesEstado;
  });

  const handleVerPedido = (pedido) => {
    setSelectedPedido(pedido); // Abre modal con detalles (ahora incluye articulos)
  };

  const handleCloseModal = () => {
    setSelectedPedido(null);
  };

  const handleRetry = () => {
    setError('');
    fetchPedidos();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <motion.div
          className="text-center py-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Cargando pedidos...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.h1
        className="text-4xl font-extrabold text-gray-900 mb-8 text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Pedidos
      </motion.h1>

      {/* Mensaje de error con botón de retry si hay problemas */}
      {error && (
        <motion.div
          className="mb-6 p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-xl flex items-center gap-2 justify-between"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
            {usingFallback && <span className="text-sm font-medium">(Usando datos de ejemplo)</span>}
          </div>
          {!usingFallback && (
            <motion.button
              onClick={handleRetry}
              className="px-3 py-1 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <RefreshCw className="w-4 h-4 inline mr-1" />
              Reintentar
            </motion.button>
          )}
        </motion.div>
      )}

      {/* Filtros */}
      <div className="mb-6 bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-4 shadow-lg flex flex-col md:flex-row gap-4">
        <div className="flex items-center gap-2 flex-1">
          <Search className="w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Buscar por ID o cliente..."
            className="flex-1 p-2 bg-transparent outline-none text-gray-800 placeholder-gray-400"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-500" />
          <select
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="todos">Todos los Estados</option>
            {estados.map(estado => (
              <option key={estado.value} value={estado.value}>{estado.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista de pedidos - Tabla en desktop, cards en móvil */}
      <div className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl overflow-hidden shadow-lg">
        {filteredPedidos.length === 0 ? (
          <motion.div
            className="text-center py-12 text-gray-500"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-xl font-medium">No hay pedidos que mostrar.</p>
            {searchTerm || filterEstado !== 'todos' && (
              <p className="text-sm text-gray-400 mt-2">Intenta ajustar los filtros.</p>
            )}
          </motion.div>
        ) : (
          <>
            {/* Tabla para desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Pedido</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredPedidos.map((pedido, index) => (
                    <motion.tr
                      key={pedido.idPedido}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{pedido.idPedido}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(pedido.fechaPedido).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{pedido.cliente}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-green-600">
                        PEN {pedido.total.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadge(pedido.estado)}`}>
                          {estados.find(e => e.value === pedido.estado)?.label || pedido.estado}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        <motion.button
                          onClick={() => handleVerPedido(pedido)}
                          className="text-indigo-600 hover:text-indigo-900 p-1"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          title="Ver detalles"
                        >
                          <Eye className="w-4 h-4" />
                        </motion.button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards para móvil */}
            <div className="md:hidden space-y-4 p-4">
              {filteredPedidos.map((pedido, index) => (
                <motion.div
                  key={pedido.idPedido}
                  className="bg-gray-50 p-4 rounded-lg shadow-sm border"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">Pedido #{pedido.idPedido}</h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadge(pedido.estado)}`}>
                      {estados.find(e => e.value === pedido.estado)?.label || pedido.estado}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    <p><User className="w-4 h-4 inline mr-1" /> {pedido.cliente}</p>
                    <p><CalIcon className="w-4 h-4 inline mr-1" /> {new Date(pedido.fechaPedido).toLocaleDateString()}</p>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-lg font-semibold text-green-600">PEN {pedido.total.toFixed(2)}</p>
                    <motion.button
                      onClick={() => handleVerPedido(pedido)}
                      className="text-indigo-600 hover:text-indigo-900 p-1"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Ver detalles"
                    >
                      <Eye className="w-5 h-5" />
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}

        {/* Modal de detalles del pedido (con lista de artículos) */}
        <AnimatePresence>
          {selectedPedido && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseModal}
            >
              <motion.div
                className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">Detalles del Pedido #{selectedPedido.idPedido}</h2>
                  <motion.button
                    onClick={handleCloseModal}
                    className="text-gray-500 hover:text-gray-700 p-1"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Cerrar"
                  >
                    <X className="w-6 h-6" />
                  </motion.button>
                </div>

                {/* Datos básicos */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Cliente:</p>
                    <p className="font-semibold text-gray-900">{selectedPedido.cliente}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Fecha:</p>
                    <p className="font-semibold text-gray-900">{new Date(selectedPedido.fechaPedido).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Estado:</p>
                    <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getEstadoBadge(selectedPedido.estado)}`}>
                      {estados.find(e => e.value === selectedPedido.estado)?.label || selectedPedido.estado}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total:</p>
                    <p className="text-2xl font-bold text-green-600">PEN {selectedPedido.total.toFixed(2)}</p>
                  </div>
                </div>

                {/* Lista de artículos */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    Artículos del Pedido
                    <span className="text-sm text-gray-500">({selectedPedido.articulos?.length || 0} items)</span>
                  </h3>
                  {selectedPedido.articulos && selectedPedido.articulos.length > 0 ? (
                    <>
                      {/* Tabla para desktop */}
                      <div className="hidden md:block overflow-x-auto mb-4">
                        <table className="w-full bg-gray-50 rounded-lg">
                          <thead className="bg-white">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Precio Unit.</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {selectedPedido.articulos.map((articulo, index) => (
                              <motion.tr
                                key={articulo.codArticulo || index}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: index * 0.1 }}
                                className="hover:bg-gray-100"
                              >
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{articulo.desArticulo}</td>
                                <td className="px-4 py-3 text-center text-sm text-gray-600">{articulo.cantidad}</td>
                                <td className="px-4 py-3 text-right text-sm text-gray-600">PEN {articulo.precioUnitario?.toFixed(2) || '0.00'}</td>
                                <td className="px-4 py-3 text-right text-sm font-medium text-green-600">PEN {articulo.subtotal?.toFixed(2) || '0.00'}</td>
                              </motion.tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Lista para móvil */}
                      <div className="md:hidden space-y-3">
                        {selectedPedido.articulos.map((articulo, index) => (
                          <motion.div
                            key={articulo.codArticulo || index}
                            className="bg-white p-3 rounded-lg shadow-sm border"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                          >
                            <p className="font-medium text-gray-900 mb-1">{articulo.desArticulo}</p>
                            <div className="flex justify-between text-sm text-gray-600">
                              <span>Cantidad: {articulo.cantidad}</span>
                              <span>Unit: PEN {articulo.precioUnitario?.toFixed(2) || '0.00'}</span>
                            </div>
                            <p className="text-right font-medium text-green-600 mt-1">Subtotal: PEN {articulo.subtotal?.toFixed(2) || '0.00'}</p>
                          </motion.div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <motion.div
                      className="text-center py-8 text-gray-500"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <Minus className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No hay artículos disponibles para este pedido.</p>
                    </motion.div>
                  )}
                </div>

                <motion.button
                  onClick={handleCloseModal}
                  className="w-full bg-gray-300 text-gray-800 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cerrar
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Orders;